import React from 'react';
import { Divider, InputAdornment, MenuItem, TextField } from '@mui/material';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { useLanguage } from '../common/GeneralUtils';
import { getActiveLocations, getLocationName } from '../common/filter/locationGrouping';

const FLOATING_OPTION_VALUE = '__floating__';

type UserLocationValue = {
  locationId: string | null;
  isFloating: boolean;
};

type UserLocationSelectProps = {
  value: UserLocationValue;
  onChange: (value: UserLocationValue) => void;
  helperText: string;
  error?: boolean;
  disabled?: boolean;
};

export const UserLocationSelect = ({ value, onChange, helperText, error, disabled }: UserLocationSelectProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { account } = useGetAccountAuthenticated({});

  return (
    <TextField
      select
      fullWidth
      size="small"
      label={t('users.fields.location')}
      value={value.isFloating ? FLOATING_OPTION_VALUE : value.locationId ?? ''}
      onChange={(event) =>
        event.target.value === FLOATING_OPTION_VALUE
          ? onChange({ locationId: null, isFloating: true })
          : onChange({ locationId: event.target.value, isFloating: false })
      }
      error={error}
      disabled={disabled}
      helperText={value.isFloating && !error ? t('users.location_floating_help') : helperText}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <PlaceIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          </InputAdornment>
        ),
      }}>
      {getActiveLocations(account).map((location) => (
        <MenuItem key={location.id} value={location.id}>
          {getLocationName(location, language)}
        </MenuItem>
      ))}
      <Divider />
      <MenuItem value={FLOATING_OPTION_VALUE}>{t('users.location_floating')}</MenuItem>
    </TextField>
  );
};
